import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Package, LineChart, Building2, Sun, Moon, LogOut, ChevronDown, Check, ShoppingCart, Settings2, UserCog } from "lucide-react";
import FeeSettingsDrawer from "@/components/FeeSettingsDrawer";
import ProfileSettings from "@/components/ProfileSettings";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/skus", label: "SKU Database", icon: Package, testid: "nav-skus" },
  { to: "/sales-log", label: "Sales & Ad Log", icon: LineChart, testid: "nav-sales" },
  { to: "/orders", label: "Orders & Uploads", icon: ShoppingCart, testid: "nav-orders" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { workspaces, current, select } = useWorkspace();
  const navigate = useNavigate();
  const [feesOpen, setFeesOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-border">
          <div className="overline text-muted-foreground mb-2">MarginOps by Ask</div>
          <div className="font-display text-lg font-bold tracking-tight">Unit Economics</div>
        </div>

        {/* Workspace Switcher */}
        <div className="px-4 py-4 border-b border-border">
          <div className="overline text-muted-foreground mb-2">Workspace</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="workspace-switcher"
                className="w-full flex items-center justify-between px-3 py-2 border border-border hover:bg-accent text-left"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{current?.name || "No workspace"}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{current?.brand_name || "—"}</div>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel className="overline text-muted-foreground">Switch workspace</DropdownMenuLabel>
              {workspaces.map((w) => (
                <DropdownMenuItem key={w.id} onClick={() => select(w)} data-testid={`ws-item-${w.id}`}>
                  <div className="flex items-center justify-between w-full">
                    <span className="truncate">{w.name}</span>
                    {current?.id === w.id && <Check className="h-3.5 w-3.5 ml-2" />}
                  </div>
                </DropdownMenuItem>
              ))}
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/workspaces")} data-testid="ws-manage">
                    Manage workspaces
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm border-l-2 hover:bg-accent transition-colors ${
                    isActive ? "border-foreground bg-accent font-medium" : "border-transparent text-muted-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            );
          })}
          {isAdmin && (
            <NavLink
              to="/workspaces"
              data-testid="nav-workspaces"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 hover:bg-accent transition-colors ${
                  isActive ? "border-foreground bg-accent font-medium" : "border-transparent text-muted-foreground"
                }`
              }
            >
              <Building2 className="h-4 w-4" />
              Workspaces
            </NavLink>
          )}
        </nav>

        {/* User + theme */}
        <div className="p-4 border-t border-border space-y-3">
          <Button variant="outline" size="sm" className="w-full justify-start rounded-none" onClick={() => setFeesOpen(true)} data-testid="fee-settings-btn">
            <Settings2 className="h-3.5 w-3.5 mr-2" /> Fee Settings
          </Button>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={() => setProfileOpen(true)} data-testid="profile-settings-btn" aria-label="profile settings" title="Profile settings">
                <UserCog className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="toggle theme">
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start rounded-none"
            onClick={async () => { await logout(); navigate("/login"); }}
            data-testid="logout-btn"
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
      <FeeSettingsDrawer open={feesOpen} onOpenChange={setFeesOpen} />
      <ProfileSettings open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
